import React, { useEffect, useState, useMemo } from 'react'
import cn from 'classnames'
import { useQuery } from 'react-query'

import { useDropdown } from '@hooks/useDropdown'
import { ChevronDownIcon } from '@components/icons/ChevronDownIcon'
import { S3_URL } from '@configs/config'
import {
  AddressSearchSelectProps,
  ThailandAddressType,
} from './interfaces/AddressSearchSelect'

export const AddressSearchSelect = React.memo(
  ({
    name,
    addressKey,
    placeholder,
    className,
    disabled,
    value,
    onChange,
  }: AddressSearchSelectProps) => {
    const [round, setRound] = useState(1)
    const [textSearch, setTextSearch] = useState('')
    const { dropdownRef, isShowDropdown, toggleDropdown } = useDropdown()
    const { data: thailandAddress } = useQuery('thailand-address', () =>
      fetch(`${S3_URL}/thailandAddress.json`).then(res => res.json())
    )

    useEffect(() => {
      if (!isShowDropdown) {
        setTextSearch(String(value))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowDropdown])

    useEffect(() => {
      setTextSearch(String(value))
    }, [value])

    const thailandAddressSearchList = useMemo(() => {
      setRound(1)
      if (textSearch && thailandAddress) {
        return thailandAddress.filter((address: ThailandAddressType) =>
          String(address[addressKey]).includes(textSearch)
        )
      }
      return thailandAddress || []
    }, [textSearch, addressKey, thailandAddress])

    function handleAddressChange(address: ThailandAddressType) {
      setTextSearch(String(address[addressKey]))

      if (onChange) {
        onChange(address)
      }
    }

    return (
      <div
        ref={dropdownRef}
        className={cn(
          'font-light w-full h-[40px] border-[1px] border-gray-3 dark:border-purple rounded-[8px] text-[14px] text-secondary dark:text-dark-10 p-[10px] cursor-pointer dark:bg-dark-8',
          className,
          {
            'cursor-default pointer-events-none bg-gray-light': disabled,
            'border-primary': isShowDropdown,
          }
        )}
        onClick={toggleDropdown}
      >
        <div className='flex items-center justify-between'>
          <input
            name={name}
            className='w-full outline-none font-light placeholder:text-secondary-1 dark:bg-dark-8'
            placeholder={placeholder}
            value={textSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (
                /[\d]+$/.test(e.target.value) ||
                e.target.value === '' ||
                addressKey !== 'zipcode'
              ) {
                setTextSearch(e.target.value)
              }
            }}
            disabled={disabled}
            maxLength={addressKey === 'zipcode' ? 5 : undefined}
            autoComplete='off'
          />
          <ChevronDownIcon className='text-primary' width='16' height='16' />
        </div>
        <div
          className={cn(
            'absolute w-full z-10 overflow-y-auto mt-[13px] right-0 bg-white dark:bg-dark-8 border-1 border-gray dark:border-purple rounded-[10px] max-h-[200px] shadow-dropdown',
            {
              hidden: !thailandAddressSearchList.length || !isShowDropdown,
            }
          )}
          onScroll={(e: any) => {
            const scrollHeight = e.target.scrollHeight - 10
            const scrollTop = e.target.scrollTop + e.target.offsetHeight

            if (
              scrollHeight <= scrollTop &&
              thailandAddressSearchList.length !==
                thailandAddressSearchList.slice(0, round * 10).length
            ) {
              setRound(prev => prev + 1)
            }
          }}
        >
          {thailandAddressSearchList
            .slice(0, round * 10)
            .map((address: ThailandAddressType, index: number) => (
              <div
                className='text-secondary dark:text-dark-10 py-[10px] px-[20px] hover:bg-primary-1 dark:hover:bg-dark hover:text-primary'
                key={index}
                onClick={() =>
                  handleAddressChange(address as ThailandAddressType)
                }
              >
                {`${address.district}, ${address.amphoe}, ${address.province}, ${address.zipcode}`}
              </div>
            ))}
        </div>
      </div>
    )
  }
)
